import { apiClient } from "@api/client";
import { useBrowserOnline, useConnectionStore } from "@api/connectionStore";
import { onlineManager } from "@tanstack/react-query";
import { useEffect } from "react";

const PROBE_INTERVAL_MS = 5_000;

/** Call once, at boot. Every call goes through `apiClient`, so this middleware sees all traffic. */
export const installConnectionSensing = () => {
  // `onlineManager` assumes online until an `offline` event; unseeded, a tab opened offline burns
  // retries into `PanelError`, and `setOnline` ignores the later non-change, so reconnect never refetches.
  onlineManager.setOnline(navigator.onLine);

  apiClient.use({
    onResponse() {
      useConnectionStore.getState().markReachable();
    },
    onError({ error }) {
      // react-query aborts on unmount and supersede; that says nothing about the network.
      if (error instanceof DOMException && error.name === "AbortError") return;
      useConnectionStore.getState().markUnreachable();
    },
  });
};

/**
 * Mount once, app-wide: panels that gave up fire nothing, so something must ask whether brian is back.
 * Probes on the leading edge so a returning device isn't shown "Brian isn't answering" until the first tick.
 */
export const useReachabilityProbe = () => {
  const unreachable = useConnectionStore((s) => s.serverUnreachable);
  const browserOnline = useBrowserOnline();

  useEffect(() => {
    if (!unreachable || !browserOnline) return;
    const probe = () => void apiClient.GET("/health/live").catch(() => {});
    probe();
    const id = setInterval(probe, PROBE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [unreachable, browserOnline]);
};
